<template>
  <div class="charging-policy">
    <!-- Réservé aux gestionnaires du module (ONBOARDING_MODULE_MANAGE) : arrivé par une adresse directe, on le dit
         au lieu de charger des données refusées. -->
    <v-alert v-if="!canManage" type="info" outlined dense>{{ $t('onboarding.policy-page.manage-only') }}</v-alert>
    <template v-else>

    <charging-settings-tabs ref="tabs" @profile-saved="loadProfiles(false)" />
    <v-alert v-if="error" ref="errorAlert" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <!-- Une valeur enregistrée avant qu'elle ne soit retirée (« Au seuil ») : la publication la refuse. On la montre, même
         quand son champ est caché, et on la retire d'un clic. -->
    <v-alert v-for="refused in refusedSettings" :key="refused.key" type="warning" dense text class="mb-4">
      <div class="d-flex align-center flex-wrap" style="gap: 8px">
        <span>{{ $t('onboarding.policy-page.refused-value', { setting: refused.setting, value: refused.value }) }}</span>
        <v-spacer />
        <v-btn x-small outlined color="warning" @click="unsetConfig(refused.key)">{{ $t('onboarding.policy-page.refused-value-reset') }}</v-btn>
      </div>
    </v-alert>
    <!-- La réponse « Sa recharge à domicile » n'a aucun équipement en face : comme un refus, elle se règle avant de publier. -->
    <v-alert v-if="!loading && homeIncomplete" type="warning" dense text class="mb-4" data-home-incomplete>
      <div class="d-flex align-center flex-wrap" style="gap: 8px">
        <span>{{ homeIncompleteMessage }}</span>
        <v-spacer />
        <template v-if="canManage">
          <v-btn v-for="kind in homeKindsWithoutProfile" :key="kind" x-small outlined color="warning" @click="createProfile(kind)">
            {{ $t('onboarding.charging-policy.home.create-profile-for', { equipment: kindLabel(kind).toLowerCase() }) }}
          </v-btn>
        </template>
      </div>
    </v-alert>
    <v-skeleton-loader v-if="loading" type="card, card, card" />

    <template v-else>
      <!-- Son nom (renommable), ce qu'on en fait d'ici : inviter avec elle, et le reste dans le menu -->
      <v-card class="mb-4">
        <v-card-text class="d-flex align-center flex-wrap" style="gap: 12px">
          <v-btn icon small :to="{ name: 'organizations.charging-policies' }" :title="$t('onboarding.charging-policy.list.back')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="flex-grow-1">
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <v-text-field
                  v-if="renaming" v-model="newName" dense outlined hide-details autofocus style="max-width: 360px"
                  :label="$t('onboarding.charging-policy.list.name')"
                  @keyup.enter="rename" @keyup.esc="renaming = false" @blur="rename"
              />
              <template v-else>
                <span class="text-h6">{{ chargingPolicy ? chargingPolicy.name : $t('onboarding.charging-policy.title') }}</span>
                <v-btn v-if="chargingPolicy && canManage" icon small :title="$t('onboarding.charging-policy.rename')" @click="startRenaming">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <v-chip v-if="chargingPolicy && chargingPolicy.default" x-small color="primary" outlined>{{ $t('onboarding.charging-policy.list.default') }}</v-chip>
              <v-chip v-if="chargingPolicy && chargingPolicy.archived" x-small>{{ $t('onboarding.charging-policy.list.archived') }}</v-chip>
            </div>
            <!-- Jamais publiée : personne n'y entre encore, c'est la seule chose à savoir de ses versions ici -->
            <div v-if="!configuration || !policy" class="text-caption orange--text text--darken-3">{{ $t('onboarding.charging-policy.never-published') }}</div>
          </div>
          <v-tooltip bottom :disabled="!inviteBlocked">
            <template #activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-btn v-if="canInvite" small color="primary" outlined :disabled="!!inviteBlocked" @click="inviting = true">
                  <v-icon left small>mdi-account-plus-outline</v-icon>{{ $t('onboarding.charging-policy.invite') }}
                </v-btn>
              </div>
            </template>
            <span>{{ inviteBlocked }}</span>
          </v-tooltip>
          <v-menu v-if="chargingPolicy" bottom left offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" :title="$t('onboarding.charging-policy.more')" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item v-if="canManage" @click="startRenaming">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('onboarding.charging-policy.rename') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canManage && !chargingPolicy.default && !chargingPolicy.archived" @click="makeDefault">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-star-outline</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('onboarding.charging-policy.list.make-default') }}</v-list-item-title>
              </v-list-item>
              <!-- Reprendre la configuration de la mère remplace tout : pas tant que des modifications attendent d'être publiées. -->
              <v-list-item v-if="canManage && parentId && isOriginal" :disabled="dirty" @click="openCopyPreview">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('onboarding.policy-page.copy-from-parent') }}</v-list-item-title>
                  <v-list-item-subtitle v-if="dirty">{{ $t('onboarding.policy-page.copy-needs-published') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <audit-trail-button v-if="$route.params.policyId" class="ml-1" entity-type="ChargingPolicy,OnboardingConfiguration,CommissioningPolicy" :entity-id="$route.params.policyId" />
        </v-card-text>
      </v-card>

      <!-- En clair : ce que la politique fait, en une phrase par sujet -->
      <v-alert text color="blue-grey darken-2" class="mb-4 charging-policy__summary" icon="mdi-text-box-outline">
        <div v-for="(line, i) in summary" :key="i">{{ line }}</div>
      </v-alert>

      <!-- D4 : ne concerne que les commandes qui partent seules à son arrivée ; celles qu'un gestionnaire passe lui-même
           sont validées d'office. Rien ne part seul : rien à valider, l'interrupteur n'a pas lieu d'être. -->
      <v-card v-if="ordersAtArrival" outlined class="mb-4">
        <v-card-text class="d-flex align-center flex-wrap py-3" style="gap: 12px">
          <v-icon color="primary">mdi-clipboard-check-outline</v-icon>
          <div class="flex-grow-1">
            <div class="font-weight-bold">{{ $t('onboarding.charging-policy.orders-approval.label') }}</div>
            <div class="text-caption text--secondary">{{ $t('onboarding.charging-policy.orders-approval.explained') }}</div>
          </div>
          <v-switch :input-value="!!policyValues[APPROVAL]" dense hide-details class="mt-0 pt-0" @change="setPolicy(APPROVAL, !!$event)" />
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="12" md="8">
          <!-- Ce que reçoit la personne, en tiroirs : chacun dit en une ligne ce qu'il règle. -->
          <v-expansion-panels v-model="openDrawers" multiple class="mb-4 drawers">
            <!-- 1. Son adhésion -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="1" :title="$t('onboarding.charging-policy.drawer.membership')" :summary="drawerSummary.membership" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <setting-field
                    v-for="key in personKeys" :key="key"
                    :definition="configDefinition(key)" :label="labelOf(key)"
                    :value="configValues[key]" :explicit="!!configExplicit[key]"
                    :options="configOptions(key)" :picker="pickers[key] || null"
                    :forced="key === LINK_APPROVAL && approvalForced ? true : null"
                    :forced-hint="$t('onboarding.charging-policy.person.link-approval-forced')"
                    @set="setConfig(key, $event)" @unset="unsetConfig(key)"
                />
                <p class="text-caption text--secondary mb-0 mt-2">{{ $t('onboarding.charging-policy.person.link') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 2. Ses badges : toujours inactifs au départ, c'est elle qui les active -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="2" :title="$t('onboarding.charging-policy.badges.title')" :summary="drawerSummary.badges" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="text-body-2 mb-1">{{ $t('onboarding.charging-policy.badges.supply-question') }}</div>
                <v-radio-group :value="badgeChoice" dense hide-details class="mt-0" @change="setBadgeChoice">
                  <v-radio v-for="value in BADGE_CHOICES" :key="value" :value="value" :label="$t(`onboarding.commissioning.badge-supply.${value}`)" />
                </v-radio-group>
                <template v-if="badgeChoice !== 'none'">
                  <div class="text-body-2 mt-4 mb-1">{{ $t('onboarding.charging-policy.badges.dispatch-title') }}</div>
                  <v-radio-group :value="policyValues[BADGE_DISPATCH] || 'zeborne'" dense hide-details class="mt-0" @change="setPolicy(BADGE_DISPATCH, $event)">
                    <v-radio v-for="value in ['zeborne', 'organization']" :key="value" :value="value" :label="$t(`onboarding.charging-policy.badges.dispatch.${value}`)" />
                  </v-radio-group>
                  <p class="text-caption text--secondary mb-0 mt-1">{{ $t(`onboarding.charging-policy.badges.dispatch-explained.${policyValues[BADGE_DISPATCH] || 'zeborne'}`) }}</p>
                  <p class="text-caption text--secondary mb-0 mt-1">{{ $t('onboarding.charging-policy.badges.inactive') }}</p>
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 3. Sa recharge à domicile : remboursée ou non, avec quel équipement, quand -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="3" :title="$t('onboarding.charging-policy.home.title')" :summary="drawerSummary.home" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="text-body-2 mb-1">{{ $t('onboarding.charging-policy.home.mode-question') }}</div>
                <v-radio-group :value="homeMode" dense hide-details class="mt-0" @change="setHomeMode">
                  <v-radio v-for="mode in HOME_MODES" :key="mode" :value="mode" :label="$t(`onboarding.charging-policy.home.mode.${mode}`)" />
                </v-radio-group>

                <template v-if="homeMode !== 'none'">
                  <div class="text-body-2 mt-4 mb-1">{{ $t('onboarding.charging-policy.home.equipment-question') }}</div>
                  <div v-for="kind in homeOptions" :key="kind" class="kind" :class="{ 'kind--off': !enabled(kind) }">
                    <div class="kind__head">
                      <v-icon class="mr-2" :color="enabled(kind) ? 'primary' : 'grey'">{{ kindIcon(kind) }}</v-icon>
                      <div class="kind__text">
                        <div class="kind__name">{{ $t(`onboarding.charging-policy.home.option.${kind}`) }}</div>
                        <div class="text-caption text--secondary">{{ $t(`onboarding.charging-policy.home.option-explained.${kind}`) }}</div>
                      </div>
                      <!-- Aucun profil qui convienne : on le crée d'ici, il est désigné aussitôt -->
                      <v-switch
                          v-if="homeProfiles(kind).length || !canManage"
                          :input-value="enabled(kind)" :disabled="!homeProfiles(kind).length"
                          dense hide-details class="mt-0 pt-0" @change="toggleHome(kind, $event)"
                      />
                      <v-btn v-else small outlined color="primary" @click="createProfile(kind)">
                        <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.charging-policy.create-profile') }}
                      </v-btn>
                    </div>
                    <div v-if="enabled(kind)" class="kind__body">
                      <!-- Une ligne par type : « Tous les types », et les types qui ont leur propre profil. -->
                      <template v-if="deviceTypesOf(kind).length">
                        <div v-for="row in deviceTypeRows(kind)" :key="row.type" class="kind__typed">
                          <v-select
                              class="kind__typed-type"
                              :value="row.type" :items="rowTypeOptions(kind, row.type)"
                              :label="$t(`onboarding.commissioning.form.device-type.${kind}`)" outlined dense hide-details
                              @change="changeRowType(kind, row.type, $event)"
                          />
                          <v-select
                              :value="row.profileId" :items="profileOptions(kind, rowDeviceType(row.type))" item-value="id" :item-text="profileText"
                              :label="$t('onboarding.charging-policy.profile')" outlined dense hide-details
                              @change="setRowProfile(kind, row.type, $event)"
                          />
                          <v-btn v-if="deviceTypeRows(kind).length > 1" icon small :aria-label="$t('onboarding.charging-policy.remove-device-type')" @click="setRowProfile(kind, row.type, null)">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <div v-if="fieldErrors[`member:${kind}`]" class="text-caption error--text">{{ fieldErrors[`member:${kind}`] }}</div>
                        <v-menu v-if="addableRowTypes(kind).length" bottom offset-y>
                          <template #activator="{ on, attrs }">
                            <v-btn small text color="primary" class="px-0 mt-2" v-bind="attrs" v-on="on">
                              <v-icon left small>mdi-plus</v-icon>{{ $t(`onboarding.charging-policy.add-device-type.${kind}`) }}
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item v-for="option in addableRowTypes(kind)" :key="option.value" @click="addRow(kind, option.value)">
                              <v-list-item-title>{{ option.text }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                        <p class="text-caption text--secondary mb-0">{{ $t('onboarding.charging-policy.device-types-hint') }}</p>
                      </template>
                      <v-select
                          v-else
                          :value="designated(kind)" :items="profileOptions(kind)" item-value="id" :item-text="profileText"
                          :label="$t('onboarding.charging-policy.profile')"
                          outlined dense :hide-details="!fieldErrors[`member:${kind}`]"
                          :error-messages="fieldErrors[`member:${kind}`] || []"
                          @change="setDesignated(kind, $event || null)"
                      />
                      <v-btn v-if="canManage" small text color="primary" class="px-0" @click="createProfile(kind)">
                        <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.charging-policy.create-profile') }}
                      </v-btn>
                    </div>
                  </div>
                  <p class="text-caption text--secondary mb-0">
                    {{ $t('onboarding.charging-policy.home.profiles-hint') }}
                    <router-link :to="{ name: 'organizations.equipment-profiles' }">{{ $t('onboarding.charging-policy.manage-profiles') }}</router-link>
                  </p>

                  <div class="text-body-2 mt-4 mb-1">{{ $t('onboarding.charging-policy.home.when-question') }}</div>
                  <v-radio-group :value="homeChoice ? 'manager' : homeWhen" dense hide-details class="mt-0" :disabled="!designatedHomeKinds.length" @change="setHomeWhen">
                    <v-radio value="arrival" :label="$t('onboarding.charging-policy.home.when.arrival')" :disabled="homeChoice" />
                    <v-radio value="manager" :label="$t('onboarding.charging-policy.home.when.manager')" />
                  </v-radio-group>
                  <!-- Plusieurs équipements proposés : un seul compte sa recharge, le gestionnaire choisit lequel à l'invitation -->
                  <p v-if="homeChoice" class="text-caption text--secondary mt-1 mb-0">{{ $t('onboarding.charging-policy.home.one-meter') }}</p>

                  <!-- D1 : qui dit où poser la borne ou le compteur. Le technicien peut toujours poser ailleurs, sur place. -->
                  <template v-if="installedHomeKinds.length">
                    <div class="text-body-2 mt-4 mb-1">{{ $t('onboarding.charging-policy.location-choice.question') }}</div>
                    <v-radio-group :value="locationChoice" dense hide-details class="mt-0" :disabled="!canManage" @change="setLocationChoice">
                      <v-radio v-for="choice in LOCATION_CHOICES" :key="choice" :value="choice" :label="$t(`onboarding.charging-policy.location-choice.${choice}`)" />
                    </v-radio-group>
                    <v-autocomplete
                        v-if="locationChoice === 'imposed'"
                        :value="policyValues[DEFAULT_LOCATION_KEY] || null"
                        :items="sites" item-value="id" item-text="label"
                        :label="$t('onboarding.charging-policy.location-choice.site')"
                        :error-messages="policyValues[DEFAULT_LOCATION_KEY] ? [] : [$t('onboarding.charging-policy.location-choice.site-required')]"
                        :disabled="!canManage"
                        outlined dense hide-details="auto" class="mt-2"
                        @change="setPolicy(DEFAULT_LOCATION_KEY, $event || null)"
                    />
                    <p class="text-caption text--secondary mt-1 mb-0">{{ $t(`onboarding.charging-policy.location-choice.explained.${locationChoice}`) }}</p>
                  </template>
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 4. Sa recharge au travail : lue dans les profils de site qui ouvrent leurs bornes à ses groupes (pas recopiée) -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="4" :title="$t('onboarding.charging-policy.drawer.work')" :summary="drawerSummary.work" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text-caption text--secondary">{{ $t('onboarding.charging-policy.work.explained') }}</p>
                <div v-for="row in workAccess" :key="row.profile.id" class="d-flex align-center mb-1" style="gap: 8px">
                  <v-icon small>mdi-ev-station</v-icon>
                  <span class="font-weight-medium">{{ row.profile.name }}</span>
                  <v-chip x-small outlined color="primary">{{ $t(`onboarding.equipment-profile.usage.${row.profile.usage}`) }}</v-chip>
                  <span class="text-caption text--secondary">{{ $t(row.free ? 'onboarding.equipment-profile.access-free' : 'onboarding.equipment-profile.access-paid') }} · {{ row.groups }}</span>
                </div>
                <!-- Ses groupes de recharge gratuite ou privée : les bornes des organisations qu'ils nomment, et de leurs filiales -->
                <div v-for="row in workGroups" :key="row.group.id" class="d-flex align-center flex-wrap mb-1" style="gap: 8px">
                  <v-icon small>mdi-account-group-outline</v-icon>
                  <span class="font-weight-medium">{{ row.group.name }}</span>
                  <v-chip x-small outlined :color="row.free ? 'green' : 'primary'">{{ $t(row.free ? 'onboarding.equipment-profile.access-free' : 'onboarding.equipment-profile.access-paid') }}</v-chip>
                  <span class="text-caption text--secondary">{{ $t('onboarding.charging-policy.work.group-sites', { sites: row.organizations.map(id => organizationNames[id] || '…').join(', ') }) }}</span>
                </div>
                <p v-if="!workAccess.length && !workGroups.length" class="text-caption text--secondary mb-0">{{ $t('onboarding.charging-policy.work.none') }}</p>
                <router-link :to="{ name: 'organizations.equipment-profiles' }" class="text-caption">{{ $t('onboarding.charging-policy.manage-profiles') }}</router-link>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 5. Sa recharge sur le réseau public : le badge pro, aux frais de l'organisation -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="5" :title="$t('onboarding.charging-policy.drawer.public')" :summary="drawerSummary.public" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <setting-field
                    v-if="givesProfessionalBadge"
                    :definition="policyDefinition(BADGE_ROAMING)" :label="$t('onboarding.policy.setting.badges.roaming')"
                    :value="policyValues[BADGE_ROAMING]" :explicit="!!policyExplicit[BADGE_ROAMING]" :options="policyOptions(BADGE_ROAMING)"
                    @set="setPolicy(BADGE_ROAMING, $event)" @unset="unsetPolicy(BADGE_ROAMING)"
                />
                <!-- M14 : qui paie ses recharges sur le réseau public, dit en clair selon le réglage et les badges fournis. -->
                <p v-if="givesProfessionalBadge" class="text-caption text--secondary mt-2 mb-0">{{ publicPayer }}</p>
                <p v-else class="text-caption text--secondary mb-0">{{ $t('onboarding.charging-policy.public.no-professional-badge') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 6. Son véhicule : le suivi du véhicule connecté (le véhicule est à elle, jamais à l'organisation) -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="6" :title="$t('onboarding.charging-policy.drawer.vehicle')" :summary="drawerSummary.vehicle" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="kind" :class="{ 'kind--off': !designated(CONNECTED_VEHICLE) }">
                  <div class="kind__head">
                    <v-icon class="mr-2" :color="designated(CONNECTED_VEHICLE) ? 'primary' : 'grey'">{{ kindIcon(CONNECTED_VEHICLE) }}</v-icon>
                    <div class="kind__text">
                      <div class="kind__name">{{ $t('onboarding.charging-policy.vehicle.follow') }}</div>
                      <div class="text-caption text--secondary">{{ $t('onboarding.charging-policy.vehicle.explained') }}</div>
                    </div>
                    <v-switch
                        v-if="profileOptions(CONNECTED_VEHICLE).length || !canManage"
                        :input-value="!!designated(CONNECTED_VEHICLE)" :disabled="!profileOptions(CONNECTED_VEHICLE).length"
                        dense hide-details class="mt-0 pt-0" @change="toggleHome(CONNECTED_VEHICLE, $event)"
                    />
                    <v-btn v-else small outlined color="primary" @click="createProfile(CONNECTED_VEHICLE)">
                      <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.charging-policy.create-profile') }}
                    </v-btn>
                  </div>
                  <!-- Le compteur embarqué contient déjà le suivi : le réglage sert à ceux qui ne l'ont pas (une borne, rien) -->
                  <p v-if="telemetryIncluded" class="text-caption text--secondary mt-2 mb-0">{{ $t('onboarding.charging-policy.vehicle.included') }}</p>
                  <div v-if="designated(CONNECTED_VEHICLE)" class="kind__body">
                    <v-select
                        :value="designated(CONNECTED_VEHICLE)" :items="profileOptions(CONNECTED_VEHICLE)" item-value="id" :item-text="profileText"
                        :label="$t('onboarding.charging-policy.profile')" outlined dense :hide-details="!fieldErrors[`member:${CONNECTED_VEHICLE}`]"
                        :error-messages="fieldErrors[`member:${CONNECTED_VEHICLE}`] || []"
                        @change="setDesignated(CONNECTED_VEHICLE, $event)"
                    />
                    <div class="text-body-2 mt-3 mb-1">{{ $t('onboarding.charging-policy.vehicle.when-question') }}</div>
                    <v-radio-group :value="isAnnounced(CONNECTED_VEHICLE) ? 'arrival' : 'manager'" dense hide-details class="mt-0" @change="setAnnounced(CONNECTED_VEHICLE, $event === 'arrival')">
                      <v-radio value="arrival" :label="$t('onboarding.charging-policy.vehicle.when.arrival')" />
                      <v-radio value="manager" :label="$t('onboarding.charging-policy.vehicle.when.manager')" />
                    </v-radio-group>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 7. Ce qu'elle paie elle-même : ses recharges perso, le moyen de paiement, la facturation -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <drawer-title :number="7" :title="$t('onboarding.charging-policy.drawer.pays')" :summary="drawerSummary.pays" />
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <setting-field
                    :definition="configDefinition(PAYMENT_METHOD)" :label="$t('onboarding.charging-policy.network.payment-method')"
                    :value="configValues[PAYMENT_METHOD]" :explicit="!!configExplicit[PAYMENT_METHOD]" :options="configOptions(PAYMENT_METHOD)"
                    @set="setConfig(PAYMENT_METHOD, $event)" @unset="unsetConfig(PAYMENT_METHOD)"
                />
                <template v-if="personPays">
                  <setting-field
                      :definition="configDefinition(BILLING_MODE)" :label="$t('onboarding.charging-policy.network.billing-mode')"
                      :value="configValues[BILLING_MODE]" :explicit="!!configExplicit[BILLING_MODE]" :options="configOptions(BILLING_MODE)"
                      @set="setBillingMode($event)" @unset="unsetConfig(BILLING_MODE)"
                  />
                  <setting-field
                      :definition="configDefinition(COLLECTION_METHOD)" :label="$t('onboarding.charging-policy.network.collection-method')"
                      :value="configValues[COLLECTION_METHOD]" :explicit="!!configExplicit[COLLECTION_METHOD]" :options="collectionOptions"
                      @set="setConfig(COLLECTION_METHOD, $event)" @unset="unsetConfig(COLLECTION_METHOD)"
                  />
                  <p v-if="configValues[BILLING_MODE] === PAY_PER_USE" class="text-caption text--secondary mb-1">{{ $t('onboarding.charging-policy.network.pay-per-use-debit') }}</p>
                  <setting-field
                      :definition="configDefinition(INSTRUMENTS)" :label="$t('onboarding.charging-policy.network.instruments')"
                      :value="configValues[INSTRUMENTS]" :explicit="!!configExplicit[INSTRUMENTS]" :options="configOptions(INSTRUMENTS)"
                      @set="setConfig(INSTRUMENTS, $event)" @unset="unsetConfig(INSTRUMENTS)"
                  />
                </template>
                <p v-else class="text-caption text--secondary mb-0">{{ $t('onboarding.charging-policy.network.organization-pays') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Le reste, rarement touché -->
            <v-expansion-panel>
              <v-expansion-panel-header>{{ $t('onboarding.charging-policy.advanced') }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <setting-field
                    v-for="key in ADVANCED_CONFIG" :key="key"
                    :definition="configDefinition(key)" :label="labelOf(key)"
                    :value="configValues[key]" :explicit="!!configExplicit[key]" :options="configOptions(key)"
                    @set="setConfig(key, $event)" @unset="unsetConfig(key)"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <!-- Ce que verra la personne : ses services, et ce qu'il lui reste à faire pour chacun -->
        <v-col cols="12" md="4">
          <invitation-link-card v-if="chargingPolicy && !chargingPolicy.archived" :organization-id="organizationId" :policy-id="policyId" :approval="publishedLinkApproval" with-qr />
          <v-card outlined class="charging-policy__preview">
            <v-card-title class="text-subtitle-1 font-weight-bold">{{ $t('onboarding.charging-policy.preview.title') }}</v-card-title>
            <v-card-text>
              <p v-if="linkApproval" class="text-caption text--secondary">
                <v-icon x-small class="mr-1">mdi-account-check-outline</v-icon>{{ $t('onboarding.charging-policy.preview.link-approval') }}
              </p>
              <div v-for="service in preview" :key="service.key" class="preview-service" :class="{ 'preview-service--later': service.later }">
                <div class="d-flex align-center">
                  <v-icon small class="mr-2">{{ service.icon }}</v-icon>
                  <span class="font-weight-bold">{{ service.title }}</span>
                </div>
                <div class="text-caption text--secondary">{{ service.detail }}</div>
              </div>
              <p v-if="!preview.length" class="text-caption text--secondary mb-0">{{ $t('onboarding.charging-policy.preview.nothing') }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Toujours en bas de l'écran : ce qui n'est pas publié, l'annuler ou le publier -->
      <v-card class="policy-actions" :class="{ 'policy-actions--dirty': dirty }" elevation="6">
        <v-card-text class="d-flex align-center flex-wrap py-2" style="gap: 8px">
          <v-icon small :color="dirty ? 'orange darken-2' : 'green'">{{ dirty ? 'mdi-circle-edit-outline' : 'mdi-check-circle-outline' }}</v-icon>
          <span class="text-body-2" :class="{ 'font-weight-bold': dirty }">{{ dirty ? $t('onboarding.policy-page.unsaved') : $t('onboarding.charging-policy.all-published') }}</span>
          <!-- Le refus se lit là où l'on a cliqué : le message complet est en tête de page ou sous le champ fautif -->
          <span v-if="refusal" class="text-body-2 error--text policy-actions__refusal">
            <v-icon small color="error">mdi-alert-circle-outline</v-icon> {{ $t('onboarding.policy-page.publish-refused') }} {{ refusal }}
            <a class="ml-1" @click="showRefusal">{{ $t('onboarding.policy-page.show-refusal') }}</a>
          </span>
          <v-spacer />
          <v-btn small text :disabled="!dirty || publishing" @click="discard">{{ $t('cancel') }}</v-btn>
          <span v-if="homeIncomplete" class="text-body-2 error--text mr-2">{{ $t('onboarding.charging-policy.home.publish-blocked') }}</span>
          <v-btn small color="primary" :loading="publishing" :disabled="!dirty || homeIncomplete" @click="publish">
            <v-icon left small>mdi-content-save</v-icon>{{ $t('onboarding.policy-page.publish') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </template>

    <!-- Créer le profil qui manque, sans quitter la politique : type imposé, à domicile ; désigné à l'enregistrement -->
    <!-- Monté seulement ouvert : la page et sa mise en route ont chacune le leur, jamais deux à la fois dans la page. -->
    <equipment-profile-editor
        v-if="canManage && profileEditor.open"
        v-model="profileEditor.open"
        :organization-id="organizationId"
        :kind="profileEditor.kind"
        usage="home"
        :device-types="profileEditor.deviceTypes"
        :licence-types="profileEditor.licenceTypes"
        @saved="profileCreated"
    />

    <!-- Inviter avec cette politique : le dialogue des membres, la politique déjà choisie -->
    <create-invitation-dialog v-if="canInvite" v-model="inviting" :organization-id="organizationId" :policy-id="policyId" />

    <!-- Historique de l'inscription -->
    <!-- Aperçu de la copie : on montre avant d'écraser -->
    <v-dialog v-model="copy.open" max-width="760">
      <v-card>
        <v-card-title>{{ $t('onboarding.policy-page.copy-from-parent') }}</v-card-title>
        <v-card-text>
          <v-alert v-if="copy.error" type="error" dense outlined>{{ copy.error }}</v-alert>
          <template v-else-if="copy.preview">
            <p>{{ $t('onboarding.configuration.copy-explained', { version: copy.preview.fromVersion }) }}</p>
            <div v-if="!Object.keys(copy.preview.changes).length" class="text--secondary">{{ $t('onboarding.configuration.copy-changes-nothing') }}</div>
            <v-simple-table v-else dense>
              <template #default>
                <thead>
                  <tr>
                    <th>{{ $t('onboarding.registration.setting') }}</th>
                    <th>{{ $t('onboarding.configuration.today') }}</th>
                    <th>{{ $t('onboarding.configuration.after-copy') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(change, key) in copy.preview.changes" :key="key">
                    <td>{{ labelOf(key) }}</td>
                    <td>{{ render(key, change.before) }}</td>
                    <td><strong>{{ render(key, change.after) }}</strong></td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="copy.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!copy.preview" :loading="copy.running" @click="confirmCopy">{{ $t('onboarding.configuration.copy-confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import OnboardingConfigurationRepository from '@repository/OnboardingConfigurationRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import InvitationLinkCard from './Members/InvitationLinkCard.vue'
import CreateInvitationDialog from './Members/CreateInvitationDialog.vue'
import ChargingSettingsTabs from './ChargingSettingsTabs.vue'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import OrganizationRepository from '@repository/OrganizationRepository'
import OnboardingSettingsMixin from './OnboardingSettingsMixin'
import CommissioningMixin, { REFUND_LICENCE } from './CommissioningMixin'
import EquipmentProfileEditor from './EquipmentProfileEditor.vue'
import { deviceTypesByKind, equipmentKinds, KINDS_KEY, policyCatalogue as readPolicyCatalogue, PROFILES_BY_DEVICE_TYPE_KEY, PROFILES_KEY } from './Commissioning/policyCatalogue'
import SettingField from './Configuration/SettingField.vue'

/** L'en-tête d'un tiroir : son numéro, son titre, ce qu'il règle en une ligne. */
const DrawerTitle = {
  functional: true,
  props: { number: Number, title: String, summary: String },
  render(h, { props }) {
    return h('div', { class: 'drawer-title' }, [
      h('span', { class: 'drawer-title__number' }, String(props.number)),
      h('div', [h('div', { class: 'drawer-title__name' }, props.title), h('div', { class: 'drawer-title__summary' }, props.summary || '')]),
    ])
  },
}

const LINK_APPROVAL = 'membership.link-approval'
const PERSON_KEYS = ['membership.member-type', 'membership.groups', 'membership.external-reference-required', 'membership.phone-required', LINK_APPROVAL]
const ADVANCED_CONFIG = ['notifications.link-lifetime-days', 'notifications.first-reminder-days', 'notifications.second-reminder-days', 'notifications.registration-lifetime-days']
const ANNOUNCED = 'membership.equipment'
const PAYMENT_METHOD = 'payment.method'
const INSTRUMENTS = 'payment.instruments'
const BILLING_MODE = 'payment.billing-mode'
const COLLECTION_METHOD = 'payment.collection-method'
const PAY_PER_USE = 'pay-per-use'
const AUTO_DEBIT = 'auto-debit'

const APPROVAL = 'orders.approval'
/** D1 : qui dit où poser (LocationChoice côté serveur), et l'emplacement imposé. */
const LOCATION_CHOICE = 'orders.location-choice'
const LOCATION_CHOICES = ['person', 'imposed', 'at-installation']
const DEFAULT_LOCATION_KEY = 'organization.default-location'
const BADGE_SUPPLY = 'badges.supply'
const BADGE_ROAMING = 'badges.roaming'
const BADGE_DISPATCH = 'badges.dispatch'
/**
 * Ce que la politique ne règle plus (2026-10-15) : l'acompte va sur la commande, l'usage des données se déduit de
 * la licence, la borne appartient toujours à l'organisation, le courrier accompagne un envoi postal. Une valeur
 * encore posée est retirée à la prochaine publication.
 */
const RETIRED_POLICY_KEYS = ['deposit.amounts', 'consent.data-uses', 'ownership.member', 'ownership.organization', 'badges.letter', 'licences.member', 'licences.organization']

const BADGE = 'badge'
/** Les groupes qui ouvrent des bornes : recharge gratuite, ou privée (CPO privé, payante). */
const WORK_GROUP_TYPES = ['free-charge', 'private-charge']
const CONNECTED_VEHICLE = 'connected-vehicle'
/** Ce qu'une personne peut avoir chez elle. */
const HOME_KINDS = ['charging-point', 'meter-device', 'vehicle-meter']
/** Sa recharge à domicile : non, remboursée, ou une borne fournie sans remboursement. */
const HOME_MODES = ['none', 'refunded', 'provided']
/* La ligne « Tous les types » d'un équipement à sous-types : le profil `profiles.member`. */
const ALL_DEVICE_TYPES = 'all'
/** Avec quel équipement, selon la réponse : rembourser se fait par une borne, un compteur ou le véhicule ; fournir, par une borne. */
const HOME_OPTIONS = { refunded: HOME_KINDS, provided: ['charging-point'], none: [] }
/** Lui fournir : aucun badge, un badge pro, un badge perso, les deux. */
const BADGE_CHOICES = ['none', 'professional', 'personal', 'both']
const KIND_ICONS = {
  'charging-point': 'mdi-ev-station',
  'meter-device': 'mdi-meter-electric-outline',
  'connected-vehicle': 'mdi-car-connected',
  'vehicle-meter': 'mdi-car-electric-outline',
  badge: 'mdi-card-account-details-outline',
}

/**
 * La politique de recharge : ce que reçoit la personne, en tiroirs (spec section 12) — son adhésion, ses badges, sa
 * recharge à domicile, au travail, sur le réseau public, son véhicule, ce qu'elle paie. Jamais une licence ni un
 * abonnement : chaque équipement à domicile désigne un profil d'équipement, qui les porte. Deux documents côté serveur
 * (l'adhésion, l'équipement), versionnés à part ; l'écran les présente ensemble et montre ce que verra la personne.
 */
export default {
  name: 'ChargingPolicy',

  components: {AuditTrailButton,  ChargingSettingsTabs, CreateInvitationDialog, DrawerTitle, EquipmentProfileEditor, InvitationLinkCard, SettingField },

  mixins: [OnboardingSettingsMixin, CommissioningMixin],

  data() {
    return {
      ADVANCED_CONFIG, PAYMENT_METHOD, INSTRUMENTS, BILLING_MODE, COLLECTION_METHOD, PAY_PER_USE,
      APPROVAL, BADGE_SUPPLY, BADGE_ROAMING, BADGE_DISPATCH, BADGE, CONNECTED_VEHICLE, HOME_MODES, BADGE_CHOICES, LINK_APPROVAL,
      LOCATION_CHOICES, DEFAULT_LOCATION_KEY,
      // Les emplacements de l'organisation (ses sites), pour l'emplacement imposé.
      sites: [],
      openDrawers: [0, 1, 2],
      homeMode: 'none',
      homeWhen: 'arrival',
      profiles: [],
      loading: false,
      publishing: false,
      chargingPolicy: null,
      renaming: false,
      newName: '',
      inviting: false,
      error: null,
      fieldErrors: {},
      refusal: null,
      organizationNames: {},
      configCatalogue: [],
      configuration: null,
      configValues: {},
      configExplicit: {},
      configDirty: false,
      policyCatalogue: [],
      policy: null,
      policyValues: {},
      policyExplicit: {},
      policyDirty: false,
      kinds: { all: [], notYetAvailable: [] },
      deviceTypesMap: {},
      profileEditor: { open: false, kind: null, deviceType: null, deviceTypes: [], licenceTypes: null },
      copy: { open: false, preview: null, error: null, running: false },
    }
  },

  computed: {
    /** Les réglages qui portent une valeur que la publication refuserait (retirée depuis), pour les montrer. */
    refusedSettings() {
      return Object.keys(this.configValues || {})
        .map(key => ({ key, values: this.refusedValuesOf(key) }))
        .filter(item => item.values.length)
        .map(item => ({
          key: item.key,
          setting: this.labelOf(item.key),
          value: item.values.map(value => this.valueLabel(value, item.key)).join(', '),
        }))
    },

    organizationId() {
      return this.$route.params.id
    },

    policyId() {
      return this.$route.params.policyId
    },

    /** Reprendre la configuration de l'organisation mère ne vaut que pour la politique d'origine (le serveur ne sait pas l'appliquer à une autre). */
    isOriginal() {
      return !!this.chargingPolicy?.original
    },

    permissions() {
      return this.currentUser?.permissions || []
    },

    canManage() {
      return this.permissions.includes('ONBOARDING_MODULE_MANAGE')
    },

    canInvite() {
      return this.permissions.includes('ONBOARDING_INVITE') || this.permissions.includes('ONBOARDING_MODULE_MANAGE')
    },

    /** Pourquoi on ne peut pas inviter avec elle maintenant : l'invitation prend la version publiée. */
    inviteBlocked() {
      if (this.chargingPolicy?.archived) return this.$t('onboarding.charging-policy.invite-archived')
      if (!this.configuration) return this.$t('onboarding.charging-policy.invite-unpublished')
      if (this.dirty) return this.$t('onboarding.charging-policy.invite-dirty')
      return null
    },

    /** Les réglages d'adhésion que le serveur connaît (un serveur plus ancien n'a pas la validation des inscriptions). */
    personKeys() {
      return PERSON_KEYS.filter(key => this.configDefinition(key))
    },

    /** Le gestionnaire valide chaque inscription arrivée par le lien (oui, par défaut). */
    linkApproval() {
      return !!this.configDefinition(LINK_APPROVAL) && (this.approvalForced || this.configValues[LINK_APPROVAL] !== false)
    },

    /** Des équipements commandés d'office à l'arrivée de la personne : les seules commandes qui partent sans un gestionnaire. */
    ordersAtArrival() {
      return (this.configValues[ANNOUNCED] || []).length > 0
    },

    /** Ce que le lien de partage fait aujourd'hui : la version publiée, pas le brouillon en cours (règle D5 comprise). */
    publishedLinkApproval() {
      if (!this.configDefinition(LINK_APPROVAL)) return false
      const { values } = this.effective(this.configCatalogue, this.configuration)
      return (values[ANNOUNCED] || []).length > 0 || values[LINK_APPROVAL] !== false
    },

    /** D5 : l'adhésion commande du matériel, le serveur impose la validation des inscriptions par le lien. */
    approvalForced() {
      return (this.configValues[ANNOUNCED] || []).length > 0
    },

    organizationName() {
      return this.$parent.organization?.name || this.$t('onboarding.charging-policy.the-organization')
    },

    parentId() {
      return this.$parent.organization?.parentId || null
    },

    dirty() {
      return this.configDirty || this.policyDirty
    },

    personPays() {
      return this.configValues[PAYMENT_METHOD] !== 'not-asked'
    },

    /** Avec quel équipement, selon que sa recharge à domicile est remboursée ou fournie. */
    homeOptions() {
      return HOME_OPTIONS[this.homeMode].filter(kind => this.kinds.all.includes(kind))
    },

    designatedHomeKinds() {
      return HOME_KINDS.filter(kind => this.enabled(kind))
    },

    locationChoice() {
      return this.policyValues[LOCATION_CHOICE] || 'person'
    },

    /** Ce qui se pose chez elle (borne, compteur) : c'est pour eux que se décide où poser. */
    installedHomeKinds() {
      return this.designatedHomeKinds.filter(kind => kind !== 'vehicle-meter')
    },

    /** Remboursée ou fournie, mais aucun équipement désigné : la publication dirait « Non » sans le dire. */
    homeIncomplete() {
      return this.homeMode !== 'none' && !this.designatedHomeKinds.length
    },

    /** Ceux qu'on peut proposer à cette réponse et pour lesquels aucun profil ne convient. */
    homeKindsWithoutProfile() {
      return this.homeOptions.filter(kind => !this.homeProfiles(kind).length)
    },

    homeIncompleteMessage() {
      const mode = this.$t(`onboarding.charging-policy.home.mode.${this.homeMode}`)
      const missing = this.homeOptions.length > 0 && this.homeKindsWithoutProfile.length === this.homeOptions.length
      return this.$t(`onboarding.charging-policy.home.incomplete.${missing ? 'no-profile' : 'not-chosen'}`, { mode })
    },

    /** Plusieurs équipements de comptage proposés : la personne n'en aura qu'un, choisi par le gestionnaire, jamais d'office. */
    homeChoice() {
      return this.designatedHomeKinds.length > 1
    },

    /** Le compteur embarqué de sa voiture contient le suivi du kilométrage : inclus pour qui le prend. */
    telemetryIncluded() {
      return !!this.designated('vehicle-meter')
    },

    badgeChoice() {
      return this.isOrderable(BADGE) && this.isAnnounced(BADGE) ? (this.policyValues[BADGE_SUPPLY] || 'professional') : 'none'
    },

    /** Qui paie ses recharges sur le réseau public avec son badge professionnel (et avec son badge personnel, s'il en a un). */
    publicPayer() {
      const roaming = this.policyValues[BADGE_ROAMING] || 'as-stocked'
      const organization = this.organizationName
      const professional = this.$t(`onboarding.charging-policy.public.payer.${roaming}`, { organization })
      return this.badgeChoice === 'both' ? `${professional} ${this.$t('onboarding.charging-policy.public.payer.personal')}` : professional
    },

    givesProfessionalBadge() {
      return ['professional', 'both'].includes(this.badgeChoice)
    },

    /**
     * Sa recharge au travail : les profils de borne privés ou partagés dont les accès nomment un des groupes que la
     * politique donne à la personne — gratuit si l'un d'eux l'est.
     */
    workAccess() {
      const groups = this.configValues['membership.groups'] || []
      return this.profiles
        .filter(p => p.kind === 'charging-point' && !p.archived && ['private', 'shared'].includes(p.usage))
        .map(profile => {
          const grants = (profile.access || []).filter(grant => groups.includes(grant.groupId))
          return { profile, free: grants.some(g => g.free), groups: grants.map(g => this.nameIn('membership.groups', g.groupId)).join(', '), any: grants.length > 0 }
        })
        .filter(row => row.any)
    },

    /**
     * Sa recharge au travail par ses groupes : un groupe de recharge gratuite ou privée nomme des organisations, et ouvre
     * leurs bornes et celles de leurs filiales (gratuites, ou payantes pour un CPO privé).
     */
    workGroups() {
      const chosen = this.configValues['membership.groups'] || []
      return (this.pickers['membership.groups'] || [])
        .filter(group => chosen.includes(group.id) && WORK_GROUP_TYPES.includes(group.type) && (group.organizations || []).length)
        .map(group => ({ group, free: group.type === 'free-charge', organizations: group.organizations.map(String) }))
    },

    /** Ce que règle chaque tiroir, en une ligne. */
    drawerSummary() {
      const t = (key, params) => this.$t(`onboarding.charging-policy.drawer-summary.${key}`, params)
      const memberType = this.configValues['membership.member-type']
      return {
        membership: [
          memberType ? t('member-type', { type: this.nameIn('membership.member-type', memberType) }) : t('membership-default'),
          this.linkApproval ? t('link-approval') : null,
        ].filter(Boolean).join(' · '),
        badges: this.$t(`onboarding.commissioning.badge-supply.${this.badgeChoice}`),
        home: this.homeMode === 'none'
          ? this.$t('onboarding.charging-policy.home.mode.none')
          : [
            this.$t(`onboarding.charging-policy.home.mode.${this.homeMode}`),
            this.designatedHomeKinds.map(k => this.kindLabel(k).toLowerCase()).join(', ') || t('home-to-choose'),
            this.installedHomeKinds.length && this.locationChoice !== 'person' ? t(`location-${this.locationChoice}`) : null,
          ].filter(Boolean).join(' · '),
        public: this.givesProfessionalBadge
          ? this.$t(`onboarding.commissioning.badge-roaming.${this.policyValues[BADGE_ROAMING] || 'as-stocked'}`)
          : t('no-professional-badge'),
        vehicle: this.designated(CONNECTED_VEHICLE) ? t('vehicle-followed') : (this.telemetryIncluded ? t('vehicle-with-meter') : t('vehicle-not-followed')),
        work: this.workAccess.length || this.workGroups.length
          ? [...this.workAccess.map(row => row.profile.name), ...this.workGroups.map(row => row.group.name)].join(', ')
          : t('no-work'),
        pays: this.personPays ? t('person-pays') : t('organization-pays'),
      }
    },

    /** À l'acte, seul le prélèvement règle chaque recharge ; au mois ou au seuil, prélèvement ou virement. */
    collectionOptions() {
      return this.configOptions(COLLECTION_METHOD).map(option => ({
        ...option,
        disabled: this.configValues[BILLING_MODE] === PAY_PER_USE && option.value !== AUTO_DEBIT,
      }))
    },

    announcedHomeKinds() {
      return this.designatedHomeKinds.filter(kind => this.isAnnounced(kind))
    },

    summary() {
      const lines = []
      lines.push(this.personPays
        ? this.$t('onboarding.charging-policy.summary.person-pays', {
          // Sans catalogue (chargement en échec), le résumé reste vide plutôt que d'empêcher l'écran d'afficher l'erreur.
          billing: String(this.valueLabel(this.configValues[BILLING_MODE], BILLING_MODE) || '').toLowerCase(),
          collection: String(this.valueLabel(this.configValues[COLLECTION_METHOD], COLLECTION_METHOD) || '').toLowerCase(),
        })
        : this.$t('onboarding.charging-policy.summary.organization-pays', { organization: this.organizationName }))
      if (this.badgeChoice !== 'none') {
        lines.push(this.$t('onboarding.charging-policy.summary.badges-announced', { badges: this.badgeSupplyLabel.toLowerCase() }))
      }
      if (this.announcedHomeKinds.length) {
        lines.push(this.$t('onboarding.charging-policy.summary.home', { equipment: this.announcedHomeKinds.map(k => this.kindLabel(k).toLowerCase()).join(', ') }))
      } else if (this.designatedHomeKinds.length) {
        lines.push(this.$t('onboarding.charging-policy.summary.home-by-manager', { equipment: this.designatedHomeKinds.map(k => this.kindLabel(k).toLowerCase()).join(', ') }))
      } else if (this.homeMode !== 'none') {
        // Le choix est fait (remboursée, borne fournie), l'équipement reste à désigner : le résumé suit le choix.
        lines.push(this.$t('onboarding.charging-policy.summary.home-incomplete', { mode: this.$t(`onboarding.charging-policy.home.mode.${this.homeMode}`).toLowerCase() }))
      } else {
        lines.push(this.$t('onboarding.charging-policy.summary.no-home'))
      }
      if (this.installedHomeKinds.length && this.locationChoice !== 'person') {
        const site = this.sites.find(item => item.id === this.policyValues[DEFAULT_LOCATION_KEY])
        lines.push(this.locationChoice === 'imposed'
          ? (site ? this.$t('onboarding.charging-policy.summary.location-imposed', { location: site.label }) : this.$t('onboarding.charging-policy.summary.location-imposed-missing'))
          : this.$t('onboarding.charging-policy.summary.location-at-installation'))
      }
      if (this.linkApproval) lines.push(this.$t('onboarding.charging-policy.summary.link-approval'))
      if (this.policyValues[APPROVAL] && this.ordersAtArrival) lines.push(this.$t('onboarding.charging-policy.summary.approval'))
      // Chaque phrase commence par une majuscule, même quand elle s'ouvre sur un libellé.
      return lines.map(line => line.charAt(0).toUpperCase() + line.slice(1))
    },

    badgeSupplyLabel() {
      const key = `onboarding.commissioning.badge-supply.${this.policyValues[BADGE_SUPPLY]}`
      return this.$te(key) ? this.$t(key) : String(this.policyValues[BADGE_SUPPLY] || '')
    },

    /** Les cartes de l'espace client : un service, et ce que la personne aura à faire pour l'activer. */
    preview() {
      const services = [{
        key: 'network',
        icon: 'mdi-ev-plug-type2',
        title: this.$t('onboarding.charging-policy.preview.network'),
        detail: this.personPays
          ? this.$t('onboarding.charging-policy.preview.network-payment')
          : this.$t('onboarding.charging-policy.preview.network-active'),
      }]
      if (this.badgeChoice !== 'none') {
        const dispatch = this.policyValues[BADGE_DISPATCH] || 'zeborne'
        services.push({ key: BADGE, icon: KIND_ICONS[BADGE], title: this.badgeSupplyLabel, detail: this.$t(`onboarding.charging-policy.preview.badges-${dispatch}`) })
      }
      const approval = this.policyValues[APPROVAL] ? ` ${this.$t('onboarding.charging-policy.preview.after-approval')}` : ''
      for (const kind of this.designatedHomeKinds) {
        // Commandé dès son arrivée : ses étapes ; quand le gestionnaire le décide : rien d'office, seulement s'il le commande.
        const later = !this.isAnnounced(kind)
        services.push({
          key: kind,
          icon: KIND_ICONS[kind],
          title: this.kindLabel(kind),
          detail: later ? this.$t('onboarding.charging-policy.preview.if-ordered') : this.$t(`onboarding.charging-policy.preview.steps.${kind}`) + approval,
          later,
        })
      }
      return services
    },
  },

  watch: {
    // Le compte connu après coup (rechargement de la page) : on charge dès que le droit est su.
    canManage(on) {
      if (on) this.load()
    },
    workGroups(rows) {
      this.loadOrganizationNames(rows.flatMap(row => row.organizations))
    },
    // D'une politique à l'autre sans quitter l'écran. Gardé en cache (keep-alive), l'écran voit aussi les routes
    // qui ne sont pas les siennes : sans politique dans l'adresse (retour arrière vers une autre page), rien à charger.
    policyId(id) {
      if (id && this.canManage) {
        this.load()
      }
    },
  },

  created() {
    if (this.canManage) this.load()
    window.addEventListener('beforeunload', this.warnBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
  },

  // Des modifications non publiées : on ne les perd pas sans l'avoir dit, ni vers un autre écran, ni vers une autre politique.
  async beforeRouteLeave(to, from, next) {
    next(await this.mayLeave())
  },

  async beforeRouteUpdate(to, from, next) {
    next(to.params.policyId === from.params.policyId || await this.mayLeave())
  },

  methods: {
    async mayLeave() {
      if (!this.dirty) return true
      return !!(await this.$dialog.confirm({
        title: this.$t('onboarding.charging-policy.leave.title'),
        text: this.$t('onboarding.charging-policy.leave.text'),
        persistent: true,
      }))
    },

    warnBeforeUnload(event) {
      if (!this.dirty) return
      event.preventDefault()
      event.returnValue = ''
    },

    async load() {
      this.loading = true
      this.error = null
      this.fieldErrors = {}
      this.refusal = null
      try {
        const [configCatalogue, configuration, policyCatalogue, policy, kinds, policies, deviceTypes] = await Promise.all([
          OnboardingConfigurationRepository.catalogue(),
          OnboardingConfigurationRepository.get(this.organizationId, null, this.policyId),
          readPolicyCatalogue(),
          CommissioningPolicyRepository.get(this.organizationId, null, this.policyId),
          equipmentKinds(),
          ChargingPolicyRepository.list(this.organizationId, true),
          deviceTypesByKind(),
        ])
        this.deviceTypesMap = deviceTypes || {}
        this.chargingPolicy = policies.find(p => p.id === this.policyId) || null
        this.configCatalogue = configCatalogue
        this.policyCatalogue = policyCatalogue
        this.kinds = kinds
        this.applyConfig(configuration)
        this.applyPolicy(policy)
        await this.loadPickers(this.organizationId)
        this.loadSites()
        await this.loadProfiles()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    /** Les sites de l'organisation, pour l'emplacement imposé (le parking de la copropriété), jamais le domicile d'une personne. */
    async loadSites() {
      try {
        const sites = await CommissioningPolicyRepository.sites(this.organizationId)
        this.sites = (sites || []).map(l => ({ id: l.id, label: [l.name, [l.address?.street, l.address?.city].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || l.id }))
      } catch (e) {
        this.sites = []
      }
    },

    setLocationChoice(choice) {
      this.setPolicy(LOCATION_CHOICE, choice)
      // L'emplacement imposé ne sert qu'à ce choix : il ne reste pas en vigueur pour autre chose.
      if (choice !== 'imposed') this.unsetPolicy(DEFAULT_LOCATION_KEY)
    },

    /**
     * Annuler : revenir à ce qui est publié, gardé en mémoire depuis le chargement (ou la dernière publication). Rien à
     * relire, pas d'écran de chargement : seules les modifications en cours disparaissent.
     */
    discard() {
      this.applyConfig(this.configuration)
      this.applyPolicy(this.policy)
      this.fieldErrors = {}
      this.refusal = null
      this.deriveHome()
    },

    /**
     * Les profils d'équipement de l'organisation (archivés compris : un profil désigné puis archivé se montre). Relus
     * après une création (`derive` faux), les réponses en cours de saisie restent.
     */
    async loadProfiles(derive = true) {
      this.profiles = await EquipmentProfileRepository.list(this.organizationId, true).catch(() => [])
      if (derive) this.deriveHome()
    },

    /** Les réponses de « Sa recharge à domicile », lues dans ce que la politique désigne et annonce. */
    deriveHome() {
      const designated = this.designatedHomeKinds
        .flatMap(kind => [this.designated(kind), ...Object.values(this.typedProfiles(kind))])
        .map(id => this.profileOf(id)).filter(Boolean)
      if (!designated.length) this.homeMode = 'none'
      else this.homeMode = designated.some(p => p.licence.licenceType === REFUND_LICENCE) ? 'refunded' : 'provided'
      this.homeWhen = !this.designatedHomeKinds.length || this.announcedHomeKinds.length ? 'arrival' : 'manager'
    },

    profileOf(id) {
      return this.profiles.find(p => p.id === id) || null
    },

    /** Le profil « tous les sous-types » désigné pour ce type. */
    designated(kind) {
      return (this.policyValues[PROFILES_KEY] || {})[kind] || null
    },

    /** Les profils désignés par sous-type pour ce type : { sous-type → profil }. */
    typedProfiles(kind) {
      return (this.policyValues[PROFILES_BY_DEVICE_TYPE_KEY] || {})[kind] || {}
    },

    /** Le type est donné à la personne : un profil « tous », ou au moins un par sous-type. */
    enabled(kind) {
      return !!this.designated(kind) || Object.values(this.typedProfiles(kind)).some(Boolean)
    },

    deviceTypesOf(kind) {
      return this.deviceTypesMap[kind] || []
    },

    deviceTypeLabel(deviceType) {
      const key = `onboarding.device-type.${deviceType}`
      return this.$te(key) ? this.$t(key) : deviceType
    },

    /** Les sous-types qui n'ont pas encore leur profil. */
    freeDeviceTypes(kind) {
      const typed = this.typedProfiles(kind)
      return this.deviceTypesOf(kind).filter(deviceType => !typed[deviceType])
    },

    /** Le profil répond à la question 1 : remboursé, ou non (un véhicule connecté ne rembourse rien). */
    fitsHomeMode(profile) {
      if (profile.kind === CONNECTED_VEHICLE) return true
      // « Non » : aucun équipement à domicile, ni remboursé ni fourni (une borne fournie ne reste pas désignée).
      if (this.homeMode === 'none') return false
      return (profile.licence.licenceType === REFUND_LICENCE) === (this.homeMode === 'refunded')
    },

    /** Les profils à domicile actifs de ce type qui répondent à la question 1, quels que soient leurs sous-types. */
    homeProfiles(kind) {
      return this.profiles.filter(p => p.kind === kind && p.usage === 'home' && !p.archived && this.fitsHomeMode(p))
    },

    /**
     * Les profils à domicile de ce type qui répondent à la question 1 : remboursés, ou non. Pour « tous les sous-types »,
     * ceux qui les visent tous ; pour un sous-type, ceux qui le visent (ou tous). Le désigné reste visible.
     */
    profileOptions(kind, deviceType = null) {
      const current = deviceType ? this.typedProfiles(kind)[deviceType] : this.designated(kind)
      const withTypes = this.deviceTypesOf(kind).length > 0
      return this.profiles.filter(p => {
        if (p.kind !== kind || p.usage !== 'home') return false
        if (p.archived) return p.id === current
        const types = p.deviceTypes || []
        if (withTypes && !deviceType && types.length) return false
        if (deviceType && types.length && !types.includes(deviceType)) return false
        return this.fitsHomeMode(p)
      })
    },

    profileText(profile) {
      return [profile.name, profile.licence.subscriptionName].filter(Boolean).join(' · ') + (profile.archived ? ` (${this.$t('onboarding.equipment-profile.archived')})` : '')
    },

    /**
     * Désigner un profil : l'équipement devient commandable pour la personne, et part dès son arrivée si c'est la
     * réponse à « Quand ? » ; ne plus en désigner : ni commandable, ni annoncé.
     */
    setDesignated(kind, profileId) {
      const profiles = { ...(this.policyValues[PROFILES_KEY] || {}) }
      if (profileId) profiles[kind] = profileId
      else delete profiles[kind]
      this.setPolicy(PROFILES_KEY, profiles)
      this.syncKind(kind)
    },

    /** Le profil d'un sous-type (null : le sous-type retombe sur le profil « tous »). */
    setTypedProfile(kind, deviceType, profileId) {
      const all = { ...(this.policyValues[PROFILES_BY_DEVICE_TYPE_KEY] || {}) }
      const typed = { ...(all[kind] || {}) }
      if (profileId) typed[deviceType] = profileId
      else delete typed[deviceType]
      if (Object.keys(typed).length) all[kind] = typed
      else delete all[kind]
      this.setPolicy(PROFILES_BY_DEVICE_TYPE_KEY, all)
      this.syncKind(kind)
    },

    /**
     * Un type désigné (un profil « tous » ou par sous-type) devient commandable pour la personne, et part dès son
     * arrivée si c'est la réponse à « Quand ? » ; plus rien de désigné : ni commandable, ni annoncé.
     */
    syncKind(kind) {
      const on = this.enabled(kind)
      if (on && !this.isOrderable(kind)) {
        this.setPolicy(KINDS_KEY, [...(this.policyValues[KINDS_KEY] || []), kind])
      }
      if (HOME_KINDS.includes(kind)) {
        // D'office seulement s'il n'y en a qu'un : deux équipements de comptage ne partent jamais ensemble.
        for (const home of HOME_KINDS) this.setAnnounced(home, this.enabled(home) && !this.homeChoice && this.homeWhen === 'arrival')
      }
      // Le suivi du kilométrage a son propre « Quand ? » : coupé, il n'est plus annoncé.
      if (kind === CONNECTED_VEHICLE && !on && this.isAnnounced(kind)) this.setAnnounced(kind, false)
    },

    /** Un sous-type de plus : son premier profil possible, sinon on le crée pour lui. */
    addTypedProfile(kind, deviceType) {
      const first = this.profileOptions(kind, deviceType).find(p => !p.archived)
      if (first) this.setTypedProfile(kind, deviceType, first.id)
      else this.createProfile(kind, deviceType)
    },

    /** Les lignes d'un équipement à sous-types : « Tous les types » s'il a un profil, puis chaque type qui a le sien. */
    deviceTypeRows(kind) {
      const rows = this.designated(kind) ? [{ type: ALL_DEVICE_TYPES, profileId: this.designated(kind) }] : []
      return [...rows, ...Object.entries(this.typedProfiles(kind)).map(([type, profileId]) => ({ type, profileId }))]
    },

    rowDeviceType(type) {
      return type === ALL_DEVICE_TYPES ? null : type
    },

    rowTypeLabel(type) {
      return type === ALL_DEVICE_TYPES ? this.$t('onboarding.charging-policy.all-device-types') : this.deviceTypeLabel(type)
    },

    /** Ce qu'on peut encore ajouter : « Tous les types » s'il manque, et les types sans ligne. */
    addableRowTypes(kind) {
      const types = [...(this.designated(kind) ? [] : [ALL_DEVICE_TYPES]), ...this.freeDeviceTypes(kind)]
      return types.map(value => ({ value, text: this.rowTypeLabel(value) }))
    },

    /** Le type d'une ligne : le sien, et ceux qui n'ont pas encore de ligne. */
    rowTypeOptions(kind, type) {
      return [{ value: type, text: this.rowTypeLabel(type) }, ...this.addableRowTypes(kind)]
    },

    setRowProfile(kind, type, profileId) {
      if (type === ALL_DEVICE_TYPES) this.setDesignated(kind, profileId)
      else this.setTypedProfile(kind, type, profileId)
    },

    addRow(kind, type) {
      const first = this.profileOptions(kind, this.rowDeviceType(type)).find(p => !p.archived)
      if (first) this.setRowProfile(kind, type, first.id)
      else this.createProfile(kind, this.rowDeviceType(type))
    },

    /**
     * Une autre valeur de type sur une ligne : elle garde son profil s'il vaut pour ce type, sinon elle prend le premier
     * possible ; aucun : on le crée, et la ligne reste. La nouvelle ligne d'abord : l'équipement ne se coupe pas au passage.
     */
    changeRowType(kind, from, to) {
      if (from === to) return
      const current = this.deviceTypeRows(kind).find(row => row.type === from)?.profileId
      const options = this.profileOptions(kind, this.rowDeviceType(to)).filter(p => !p.archived)
      const profile = options.find(p => p.id === current) || options[0]
      if (!profile) {
        this.createProfile(kind, this.rowDeviceType(to))
        return
      }
      this.setRowProfile(kind, to, profile.id)
      this.setRowProfile(kind, from, null)
    },

    /** Activer : le premier profil « tous » ; à défaut, un profil par sous-type, pour chacun de ses sous-types. Couper : tout. */
    toggleHome(kind, on) {
      if (!on) {
        this.setDesignated(kind, null)
        for (const deviceType of Object.keys(this.typedProfiles(kind))) this.setTypedProfile(kind, deviceType, null)
        return
      }
      const all = this.profileOptions(kind).find(p => !p.archived)
      if (all) {
        this.setDesignated(kind, all.id)
        return
      }
      const typed = this.homeProfiles(kind)[0]
      for (const deviceType of typed?.deviceTypes || []) this.setTypedProfile(kind, deviceType, typed.id)
    },

    /** Changer la réponse 1 retire ce qui n'y répond plus (un compteur quand la borne est fournie, un profil remboursé…). */
    setHomeMode(mode) {
      this.homeMode = mode
      for (const kind of HOME_KINDS) {
        const id = this.designated(kind)
        if (id && !this.profileOptions(kind).some(p => p.id === id)) this.setDesignated(kind, null)
        for (const [deviceType, typedId] of Object.entries(this.typedProfiles(kind))) {
          if (!this.profileOptions(kind, deviceType).some(p => p.id === typedId)) this.setTypedProfile(kind, deviceType, null)
        }
      }
    },

    /**
     * « + Créer un profil » : le panneau du profil, le type imposé, à domicile, la licence qui répond à la question 1
     * (rembourser, ou non) ; pour un sous-type, ce sous-type déjà choisi.
     */
    createProfile(kind, deviceType = null) {
      const licenceTypes = kind === CONNECTED_VEHICLE || this.homeMode === 'none'
        ? null
        : (this.homeMode === 'refunded' ? [REFUND_LICENCE] : ['charging-point', 'energy-provider'])
      this.profileEditor = { open: true, kind, deviceType, deviceTypes: deviceType ? [deviceType] : [], licenceTypes }
    },

    /** Le profil créé est désigné là où on l'a demandé (son sous-type, sinon « tous », sinon chacun des siens). */
    async profileCreated(id) {
      const { kind, deviceType } = this.profileEditor
      await this.loadProfiles(false)
      this.$refs.tabs?.refresh()
      const profile = this.profileOf(id)
      if (!profile || !id) return
      if (deviceType && this.profileOptions(kind, deviceType).some(p => p.id === id)) {
        this.setTypedProfile(kind, deviceType, id)
      } else if (this.profileOptions(kind).some(p => p.id === id)) {
        this.setDesignated(kind, id)
      } else if (this.fitsHomeMode(profile) && (profile.deviceTypes || []).length) {
        for (const type of profile.deviceTypes) {
          if (!this.typedProfiles(kind)[type]) this.setTypedProfile(kind, type, id)
        }
      } else {
        this.notifyWarning('onboarding.charging-policy.created-not-fitting')
      }
    },

    setHomeWhen(when) {
      this.homeWhen = when
      for (const kind of this.designatedHomeKinds) this.setAnnounced(kind, when === 'arrival' && !this.homeChoice)
    },

    /** Lui fournir : aucun badge (ni commandé, ni annoncé), ou ces badges-là, fournis dès son arrivée. */
    setBadgeChoice(value) {
      if (value === 'none') {
        this.setOrderable(BADGE, false)
        return
      }
      this.setOrderable(BADGE, true)
      this.setAnnounced(BADGE, true)
      this.setPolicy(BADGE_SUPPLY, value)
    },

    /** Ce que l'écran édite : la valeur effective de chaque réglage, et ceux qui ont été posés. */
    effective(catalogue, document) {
      const values = {}
      const explicit = {}
      for (const definition of catalogue) {
        const setting = document?.settings?.[definition.key]
        values[definition.key] = setting ? setting.value : definition.default
        explicit[definition.key] = setting ? setting.from === 'configuration' : false
      }
      return { values, explicit }
    },

    applyConfig(configuration) {
      this.configuration = configuration || null
      const { values, explicit } = this.effective(this.configCatalogue, configuration)
      this.configValues = values
      this.configExplicit = explicit
      this.configDirty = false
    },

    applyPolicy(policy) {
      this.policy = policy || null
      const { values, explicit } = this.effective(this.policyCatalogue, policy)
      this.policyValues = values
      this.policyExplicit = explicit
      this.policyDirty = false
    },

    configDefinition(key) {
      return this.configCatalogue.find(d => d.key === key) || null
    },

    policyDefinition(key) {
      return this.policyCatalogue.find(d => d.key === key) || { key, type: 'string' }
    },

    configOptions(key) {
      const allowed = this.configDefinition(key)?.allowedValues || []
      // La valeur enregistrée qui n'est plus proposée reste visible (grisée) : un champ vide ne dirait pas ce qui bloque.
      const stale = this.refusedValuesOf(key).map(value => ({
        value,
        text: `${this.valueLabel(value, key)} · ${this.$t('onboarding.policy-page.no-longer-offered')}`,
        disabled: true,
      }))
      return [...allowed.map(value => ({ value, text: this.valueLabel(value, key) })), ...stale]
    },

    /** Les valeurs d'un réglage à liste que le catalogue ne propose plus (ni n'accepte). */
    refusedValuesOf(key) {
      const definition = this.configDefinition(key)
      if (!definition || !['enum', 'enum-list'].includes(definition.type) || !definition.allowedValues) return []
      const current = this.configValues[key]
      const values = Array.isArray(current) ? current : [current]
      return values.filter(value => value !== null && value !== undefined && value !== '' && !definition.allowedValues.includes(value))
    },

    policyOptions(key) {
      const definition = this.policyDefinition(key)
      const family = {
        [BADGE_SUPPLY]: 'onboarding.commissioning.badge-supply',
        [BADGE_ROAMING]: 'onboarding.commissioning.badge-roaming',
      }[key]
      return (definition.allowedValues || []).map(value => {
        const label = family ? `${family}.${value}` : null
        return { value, text: label && this.$te(label) ? this.$t(label) : value, disabled: (definition.notYetAvailable || []).includes(value) }
      })
    },

    setConfig(key, value) {
      this.$set(this.configValues, key, value)
      this.$set(this.configExplicit, key, true)
      this.configDirty = true
    },

    unsetConfig(key) {
      this.$set(this.configValues, key, this.configDefinition(key)?.default ?? null)
      this.$set(this.configExplicit, key, false)
      this.configDirty = true
    },

    /** Passer à l'acte ramène le règlement au prélèvement : le virement n'y est pas possible. */
    setBillingMode(value) {
      this.setConfig(BILLING_MODE, value)
      if (value === PAY_PER_USE && this.configValues[COLLECTION_METHOD] !== AUTO_DEBIT) {
        this.setConfig(COLLECTION_METHOD, AUTO_DEBIT)
      }
    },

    setPolicy(key, value) {
      this.$set(this.policyValues, key, value)
      this.$set(this.policyExplicit, key, true)
      this.policyDirty = true
      if (key === PROFILES_KEY) this.fieldErrors = {}
    },

    unsetPolicy(key) {
      this.$set(this.policyValues, key, this.policyDefinition(key)?.default ?? null)
      this.$set(this.policyExplicit, key, false)
      this.policyDirty = true
    },

    isOrderable(kind) {
      return (this.policyValues[KINDS_KEY] || []).includes(kind)
    },

    isAnnounced(kind) {
      return (this.configValues[ANNOUNCED] || []).includes(kind)
    },

    kindIcon(kind) {
      return KIND_ICONS[kind] || 'mdi-package-variant-closed'
    },

    /** Ne plus proposer un équipement, c'est aussi ne plus l'annoncer. */
    setOrderable(kind, on) {
      const kinds = (this.policyValues[KINDS_KEY] || []).filter(k => k !== kind)
      this.setPolicy(KINDS_KEY, on ? [...kinds, kind] : kinds)
      if (!on && this.isAnnounced(kind)) this.setAnnounced(kind, false)
    },

    /**
     * Un équipement annoncé par une version précédente qui ne se commande plus (son profil retiré ou archivé ailleurs) :
     * l'écran ne le montre plus, on ne pourrait pas le décocher, et le serveur refuserait la publication. On le retire.
     */
    dropUnorderableAnnounced() {
      const announced = this.configValues[ANNOUNCED] || []
      const kept = announced.filter(kind => this.isOrderable(kind) && (![...HOME_KINDS, CONNECTED_VEHICLE].includes(kind) || this.enabled(kind)))
      if (kept.length !== announced.length) this.setConfig(ANNOUNCED, kept)
    },

    setAnnounced(kind, on) {
      const announced = (this.configValues[ANNOUNCED] || []).filter(k => k !== kind)
      this.setConfig(ANNOUNCED, on ? [...announced, kind] : announced)
    },

    explicitOf(values, explicit, retired = []) {
      const settings = {}
      for (const key of Object.keys(explicit)) {
        if (explicit[key] && !retired.includes(key)) settings[key] = values[key]
      }
      return settings
    },

    /** Ce qui ne se règle plus ici et qui reste posé : la publication le retire. */
    hasRetiredSettings() {
      return RETIRED_POLICY_KEYS.some(key => this.policyExplicit[key])
    },

    /**
     * La partie équipement d'abord : l'adhésion n'annonce que ce qui se commande, le serveur le vérifie.
     * Publiée ou refusée, on reste sur la fiche : la suite (inviter, relire) se fait d'ici.
     */
    async publish() {
      this.publishing = true
      this.error = null
      this.fieldErrors = {}
      this.refusal = null
      // L'adhésion se vérifie contre l'équipement publié : l'équipement part d'abord. S'il passe et que l'adhésion est
      // refusée, on le dit, pour que le gestionnaire sache ce qui est en vigueur.
      let equipmentPublished = false
      try {
        if (this.policyDirty || this.hasRetiredSettings()) {
          await CommissioningPolicyRepository.publish(this.organizationId, this.explicitOf(this.policyValues, this.policyExplicit, RETIRED_POLICY_KEYS), this.policy?.version ?? null, this.policyId)
          this.applyPolicy(await CommissioningPolicyRepository.get(this.organizationId, null, this.policyId))
          equipmentPublished = true
        }
        this.dropUnorderableAnnounced()
        if (this.configDirty) {
          await OnboardingConfigurationRepository.publish(this.organizationId, this.explicitOf(this.configValues, this.configExplicit), this.configuration?.version ?? null, this.policyId)
          this.applyConfig(await OnboardingConfigurationRepository.get(this.organizationId, null, this.policyId))
        }
        this.notifySuccess('onboarding.policy-page.published-ok')
        this.$refs.tabs?.refresh()
      } catch (e) {
        // Un refus de profil se montre sous l'équipement fautif ; le reste en tête de page.
        const parameters = e?.response?.data?.parameters || {}
        if (parameters.kind && parameters.holder) {
          this.fieldErrors = { [`${parameters.holder}:${parameters.kind}`]: this.messageOf(e) }
        } else if (e?.response?.data?.messageKey === 'onboarding.setting.value-refused' && parameters.setting) {
          // Le réglage et sa valeur en mots, pas leurs codes.
          this.error = this.$t('onboarding.policy-page.refused-value', {
            setting: this.labelOf(parameters.setting),
            value: this.valueLabel(parameters.value, parameters.setting),
          })
        } else if (['onboarding.setting.reference-not-found', 'onboarding.setting.reference-foreign'].includes(e?.response?.data?.messageKey) && parameters.setting) {
          // Un groupe, un type de membre supprimé ou d'ailleurs : le réglage par son libellé, pas son code ni un identifiant.
          this.error = this.$t('onboarding.policy-page.refused-reference', { setting: this.labelOf(parameters.setting) })
        } else {
          this.error = this.messageOf(e)
        }
        if (equipmentPublished && this.error) {
          this.error = this.$t('onboarding.policy-page.partly-published', { reason: this.error })
        }
        this.refusal = this.error || Object.values(this.fieldErrors)[0]
      } finally {
        this.publishing = false
      }
    },

    /** Le champ fautif s'il est affiché, sinon le message en tête de page. */
    showRefusal() {
      const target = this.$el.querySelector('.kind__body .error--text') || this.$refs.errorAlert?.$el || this.$el
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    startRenaming() {
      if (!this.chargingPolicy) return
      this.newName = this.chargingPolicy.name
      this.renaming = true
    },

    async rename() {
      if (!this.renaming) return
      this.renaming = false
      const name = (this.newName || '').trim()
      if (!name || name === this.chargingPolicy.name) return
      try {
        await ChargingPolicyRepository.update(this.organizationId, this.policyId, { name })
        this.chargingPolicy = { ...this.chargingPolicy, name }
      } catch (e) {
        this.error = this.messageOf(e)
      }
    },

    async makeDefault() {
      try {
        await ChargingPolicyRepository.update(this.organizationId, this.policyId, { default: true })
        this.chargingPolicy = { ...this.chargingPolicy, default: true }
        this.notifySuccess('onboarding.charging-policy.list.default-ok')
      } catch (e) {
        this.error = this.messageOf(e)
      }
    },


    async openCopyPreview() {
      if (this.dirty) return
      this.copy = { open: true, preview: null, error: null, running: false }
      try {
        this.copy.preview = await OnboardingConfigurationRepository.copyPreview(this.organizationId, this.parentId)
      } catch (e) {
        this.copy.error = this.messageOf(e)
      }
    },

    async confirmCopy() {
      this.copy.running = true
      try {
        await OnboardingConfigurationRepository.copy(this.organizationId, this.parentId, this.copy.preview.basedOnVersion)
        this.copy.open = false
        this.notifySuccess('onboarding.configuration.copied-ok')
        this.applyConfig(await OnboardingConfigurationRepository.get(this.organizationId, null, this.policyId))
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.copy.running = false
      }
    },

    /** Les noms des organisations qu'ouvrent ses groupes, chargés une fois chacune. */
    async loadOrganizationNames(ids) {
      for (const id of [...new Set(ids)].filter(id => !(id in this.organizationNames))) {
        this.$set(this.organizationNames, id, null)
        try {
          const organization = await OrganizationRepository.get(id, true)
          this.$set(this.organizationNames, id, organization?.name || id)
        } catch (e) {
          this.$set(this.organizationNames, id, id)
        }
      }
    },

    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      if (key && this.$te(key)) return this.$t(key, data.parameters || {})
      return data?.message || this.$t('error-occurred')
    },
  },
}
</script>

<style scoped>
.charging-policy__summary div + div {
  margin-top: 4px;
}
.charging-policy__preview {
  position: sticky;
  top: 16px;
}
.preview-service {
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
}
.kind {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 10px;
}
.kind--off {
  background: #fafafa;
}
.kind__head {
  display: flex;
  align-items: center;
  gap: 4px;
}
.kind__text {
  flex: 1;
  min-width: 0;
}
/* Les champs Vuetify s'étirent par défaut : en tête de fiche, ils restent à leur taille, calés à droite. */
.kind__head > .v-input {
  flex: 0 0 auto;
}
.kind__name {
  font-weight: 700;
}
.kind__body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}
.kind__typed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
/* Un nom de profil long se coupe dans son champ au lieu d'élargir la page ; étroit, les deux champs passent l'un sous l'autre. */
.kind__typed > * {
  min-width: 0;
  flex: 1 1 220px;
}
.kind__typed-type {
  flex: 0 1 220px;
}
.kind__label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
.drawers ::v-deep .v-expansion-panel-header {
  padding-top: 12px;
  padding-bottom: 12px;
}
.drawer-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.drawer-title__number {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drawer-title__name {
  font-weight: 700;
}
.drawer-title__summary {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}
.preview-service--later {
  border-style: dashed;
  opacity: 0.75;
}
.policy-actions {
  position: sticky;
  bottom: 8px;
  z-index: 2;
  margin-top: 8px;
  border-left: 4px solid #4caf50 !important;
}
.policy-actions__refusal {
  flex: 1 1 100%;
  order: 1;
}
.policy-actions--dirty {
  border-left-color: #fb8c00 !important;
  background: #fff8e1 !important;
}
</style>
